// @ts-nocheck
/**
 * Ludipek B2B card stock overlay.
 *
 * The SSR stock badge (ludipek-stock-badge.liquid) sums variant.inventory_quantity across
 * every Shopify location — that includes all countries when the shop tracks multi-country
 * bodegas. This module fetches /products/inventory/by-location, filters to the stores
 * matching the buyer's effective country, and overlays a traffic light (never a unit count) for
 * the stock the buyer can actually get:
 * regular stock across the whole market (orders route across its warehouses) plus Fecha corta
 * stock at the buyer's ACTIVE branch only (short-dated stock ships from the branch that holds it).
 *
 * The same answer drives the Fecha corta layout of a card tagged fecha-corta
 * ([data-b2b-fecha-corta]): it switches to clearance (.is-clearance — corner badge, clearance
 * price, Fecha corta variant in the add form) only when the active branch holds at least the
 * card's add quantity in FC units. Otherwise it keeps the SSR regular layout, so a clearance
 * price the buyer can't get is never shown — including when this fetch fails.
 *
 * On the Liquidación listing ([data-lp-clearance-listing]) a card left in its regular layout is
 * faded, noted "Sin liquidación en tu sucursal" and moved after the cards that are real offers
 * at the active branch — never hidden.
 *
 * When the variant a card adds has no sellable stock, its add form gives way to the SSR
 * "Ver producto" link: the badge is the one place a card says "Agotado".
 *
 * When the Shopify "Disponible" availability filter is active (filter.v.availability=1),
 * cards with zero stock for the buyer are hidden and the visible result count is updated.
 * Shopify's native filter passes products with stock in ANY location (both countries);
 * client-side hiding narrows that to the buyer's market.
 *
 * Only runs for logged-in B2B customers with a selected location — logged-out visitors
 * see the SSR total unchanged (acceptable: they can't add to cart anyway).
 */
(function () {
  var BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  var BADGE_SELECTOR = '[data-b2b-stock]';
  var FETCH_TIMEOUT_MS = 10_000;

  // Same labels as the SSR badge (ludipek-stock-badge.liquid).
  var STOCK_LEVEL_LABELS = {
    alto: 'Stock alto',
    medio: 'Stock medio',
    bajo: 'Stock bajo',
    agotado: 'Agotado',
  };

  var STORES_BY_MARKET = {
    CL: ['SCL', 'CCP', 'ZCO'],
    PE: ['PER', 'TRU', 'PIU'],
  };

  function logDebug(message, details) {
    if (details === undefined) console.debug('[b2b-card-stock]', message);
    else console.debug('[b2b-card-stock]', message, details);
  }

  function logWarn(message, details) {
    if (details === undefined) console.warn('[b2b-card-stock]', message);
    else console.warn('[b2b-card-stock]', message, details);
  }

  function getEffectiveCountry() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getEffectiveCountry) {
      return window.LudipekB2BLocation.getEffectiveCountry();
    }
    return (BOOTSTRAP && BOOTSTRAP.storeCountry) || null;
  }

  // The buyer's active branch (store_code), or null before a location is chosen — in which case no
  // branch holds Fecha corta stock for them.
  function getActiveStoreCode() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getStoreCode) {
      return window.LudipekB2BLocation.getStoreCode();
    }
    return null;
  }

  function bootstrapIsUsable() {
    if (BOOTSTRAP === null) return false;
    if (!BOOTSTRAP.apiBaseUrl) return false;
    if (!BOOTSTRAP.isLoggedIn) return false;
    // Pending approval: stock is gated together with prices — never fetch per-card counts.
    if (BOOTSTRAP.isPending === true) return false;
    if (!getEffectiveCountry()) return false;
    return true;
  }

  function isAvailabilityFilterActive() {
    var params = new URLSearchParams(window.location.search);
    return params.get('filter.v.availability') === '1';
  }

  function resolveNetsuiteId(badge) {
    var card = badge.closest('[data-netsuite-id]');
    return card ? card.getAttribute('data-netsuite-id') : null;
  }

  async function fetchByLocation(productIds) {
    var params = new URLSearchParams();
    productIds.forEach(function (id) { params.append('product_ids', String(id)); });
    var url = BOOTSTRAP.apiBaseUrl.replace(/\/$/, '') + '/products/inventory/by-location?' + params.toString();
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);
    try {
      var response = await fetch(url, {
        headers: { Accept: 'application/json', 'x-api-key': BOOTSTRAP.apiShopifyKey },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Whole sellable groups for a grouped product, or the raw unit count when sold loose. A grouped product
  // with stock below one full group has zero sellable groups — the remainder can never be bought.
  function sellableUnits(badge, units) {
    var multiple = parseInt(badge.getAttribute('data-b2b-sales-multiple'), 10);
    if (Number.isFinite(multiple) && multiple > 1) return Math.floor(units / multiple);
    return units;
  }

  function applyBadge(badge, buyableTotal) {
    var level;
    if (sellableUnits(badge, buyableTotal) === 0) level = 'agotado';
    else if (buyableTotal > 50) level = 'alto';
    else if (buyableTotal > 10) level = 'medio';
    else level = 'bajo';

    badge.className = badge.className
      .replace(/lp-stock--(alto|medio|bajo|agotado)/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    badge.classList.add('lp-stock--' + level);
    badge.setAttribute('data-level', level);

    // A traffic light, never a unit count: Ludipek does not show stock units on the storefront.
    var textEl = badge.querySelector('[data-b2b-stock-text]');
    if (textEl) textEl.textContent = STOCK_LEVEL_LABELS[level];
    badge.removeAttribute('data-b2b-stock-loading');
  }

  // Switch a fecha-corta card between its regular and clearance layouts and point its add form at the
  // matching variant, so the price on the card is the price the cart signs. CSS keys every visual off
  // .is-clearance, and the two price layouts share one grid cell, so the swap never moves the card.
  // Clearance needs the active branch to hold at least the card's add quantity in Fecha corta units.
  // Returns whether the card is now in its clearance layout.
  function applyFechaCortaLayout(card, branchFechaCorta) {
    if (!card || !card.hasAttribute('data-b2b-fecha-corta')) return false;
    var form = card.querySelector('[data-lp-add-form]');
    var variantInput = form ? form.querySelector('input[name="id"]') : null;
    var quantityInput = form ? form.querySelector('input[name="quantity"]') : null;
    var standardVariantId = variantInput ? variantInput.getAttribute('data-standard-variant-id') : null;
    var fechaCortaVariantId = variantInput ? variantInput.getAttribute('data-fecha-corta-variant-id') : null;
    // Without both variant ids the add form can't follow the layout, so the card stays regular.
    if (!standardVariantId || !fechaCortaVariantId) return false;

    var addQuantity = Math.max(1, parseInt(quantityInput ? quantityInput.value : '', 10) || 1);
    var clearance = branchFechaCorta >= addQuantity;
    card.classList.toggle('is-clearance', clearance);
    variantInput.value = clearance ? fechaCortaVariantId : standardVariantId;
    return clearance;
  }

  // Show the add form when the variant it adds has sellable stock, otherwise swap it for the SSR
  // "Ver producto" link: the badge already says "Agotado", and a disabled button repeating it was a
  // dead end. The button's disabled state follows along as a second guard.
  function syncAddAction(card, badge, variantStock) {
    if (!card) return;
    var form = card.querySelector('[data-lp-add-form]');
    var button = form ? form.querySelector('button[name="add"]') : null;
    if (!button) return;
    var sellable = sellableUnits(badge, variantStock) > 0;
    button.disabled = !sellable;
    if (sellable) button.removeAttribute('aria-disabled');
    else button.setAttribute('aria-disabled', 'true');
    var viewLink = card.querySelector('[data-b2b-card-view]');
    if (!viewLink) return;
    form.hidden = !sellable;
    viewLink.hidden = sellable;
  }

  function hideCard(badge) {
    var card = badge.closest('.lp-product-card');
    if (card) card.style.display = 'none';
  }

  // The Liquidación listing (/collections/liquidacion) marks its grid with data-lp-clearance-listing. Every
  // product there carries the fecha-corta tag, but a card whose short-dated stock sits at another branch is
  // no clearance offer for this buyer: it stays listed and buyable at its regular price (owner decision: order,
  // never hide), faded, with a note where the "Liquidación" badge would be.
  function clearanceListingOf(card) {
    return card ? card.closest('[data-lp-clearance-listing]') : null;
  }

  function markBranchClearance(card, listing, clearance) {
    card.classList.toggle('is-branch-no-clearance', !clearance);
    var note = card.querySelector('[data-b2b-branch-note]');
    if (!note && !clearance) {
      note = document.createElement('p');
      note.className = 'lp-product-card__branch-note';
      note.setAttribute('data-b2b-branch-note', '');
      note.textContent = listing.getAttribute('data-lp-clearance-note') || 'Sin liquidación en tu sucursal';
      var media = card.querySelector('.lp-product-card__media');
      if (media) media.insertAdjacentElement('afterend', note);
      else card.prepend(note);
    }
    if (note) note.hidden = clearance;
  }

  // Real offers first: move the faded cards after them, keeping each group's order. Liquid can only order a
  // page, so this does too. A sort the buyer picked is left alone, as the page's own "available first" does.
  function orderClearanceListing(listing) {
    if (new URLSearchParams(window.location.search).has('sort_by')) return;
    var pagination = listing.querySelector(':scope > .lp-plp__pagination');
    Array.from(listing.children).forEach(function (child) {
      if (child.classList.contains('is-branch-no-clearance')) listing.insertBefore(child, pagination);
    });
  }

  function updateVisibleCount() {
    var countEl = document.querySelector('.lp-plp__count strong');
    if (!countEl) return;
    var grid = document.querySelector('[data-lp-grid]');
    if (!grid) return;
    var visible = grid.querySelectorAll('.lp-product-card:not([style*="display: none"])').length;
    countEl.textContent = String(visible);
  }

  var collectedBadges = new WeakSet();

  function collectBadges(root) {
    var badges = Array.from(root.querySelectorAll(BADGE_SELECTOR));
    var byProductId = new Map();
    badges.forEach(function (badge) {
      if (collectedBadges.has(badge)) return;
      var netsuiteId = resolveNetsuiteId(badge);
      if (!netsuiteId) return;
      var productId = Number(netsuiteId);
      if (!Number.isFinite(productId) || productId <= 0) return;
      collectedBadges.add(badge);
      if (!byProductId.has(productId)) byProductId.set(productId, []);
      byProductId.get(productId).push(badge);
    });
    return byProductId;
  }

  async function overlayStock(root) {
    if (!bootstrapIsUsable()) return;

    var byProductId = collectBadges(root);
    if (byProductId.size === 0) return;

    var country = getEffectiveCountry();
    var visibleCodes = (country && STORES_BY_MARKET[country]) || null;
    var storeCode = getActiveStoreCode();
    var productIds = Array.from(byProductId.keys());
    var filterActive = isAvailabilityFilterActive();

    var payload;
    try {
      payload = await fetchByLocation(productIds);
    } catch (error) {
      logWarn('card stock fetch failed — SSR totals remain', { error: String(error) });
      return;
    }

    var hiddenCount = 0;
    var clearanceListings = new Set();
    var items = (payload && payload.items) || [];
    items.forEach(function (entry) {
      var badges = byProductId.get(Number(entry.product_id));
      if (!badges) return;
      var stores = entry.stores || [];
      if (visibleCodes !== null) {
        stores = stores.filter(function (store) { return visibleCodes.includes(store.store_code); });
      }
      var regularTotal = stores.reduce(function (sum, store) { return sum + (store.available || 0); }, 0);
      // Fecha corta stock only ships from the branch that holds it: FC units at another branch are
      // neither counted nor offered.
      var activeStore = storeCode
        ? stores.find(function (store) { return store.store_code === storeCode; })
        : null;
      var branchFechaCorta = activeStore ? (activeStore.fecha_corta_available || 0) : 0;
      var buyableTotal = regularTotal + branchFechaCorta;
      badges.forEach(function (badge) {
        var card = badge.closest('.lp-product-card');
        var clearance = applyFechaCortaLayout(card, branchFechaCorta);
        var listing = clearanceListingOf(card);
        if (listing) {
          markBranchClearance(card, listing, clearance);
          clearanceListings.add(listing);
        }
        applyBadge(badge, buyableTotal);
        syncAddAction(card, badge, clearance ? branchFechaCorta : regularTotal);
        // A grouped product with stock below one full group is unavailable — hide it under the filter too.
        if (filterActive && sellableUnits(badge, buyableTotal) === 0) {
          hideCard(badge);
          hiddenCount++;
        }
      });
    });

    clearanceListings.forEach(orderClearanceListing);

    if (filterActive && hiddenCount > 0) {
      updateVisibleCount();
      logDebug('hid ' + hiddenCount + ' cards with zero stock in ' + country);
    }

    logDebug('overlaid ' + items.length + ' card stock badges for country ' + country + ', branch ' + storeCode);
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  var rescanScheduled = false;
  function scheduleRescan() {
    if (rescanScheduled) return;
    rescanScheduled = true;
    requestAnimationFrame(function () {
      rescanScheduled = false;
      overlayStock(document);
    });
  }

  function startObserving() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches(BADGE_SELECTOR)) ||
              (node.querySelector && node.querySelector(BADGE_SELECTOR))) {
            scheduleRescan();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function clearAllLoading() {
    document.querySelectorAll('[data-b2b-stock-loading]').forEach(function (el) {
      el.removeAttribute('data-b2b-stock-loading');
    });
  }

  onReady(function () {
    overlayStock(document);
    startObserving();
    setTimeout(clearAllLoading, 8000);
  });
})();
